import { Button } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { generateTORReport } from "../api/report.js";
import { selectUserSession } from "../slices/userSession/userSessionSlice.js";
import { THREE_YEARS_IN_PAST } from "../util/dateHelper.js";
import { convert_Luxon_To_DateFns } from "../util/dateTimeFormatter.js";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE,
} from "../util/messageConstants.js";
import { validateReportShiftDate } from "../util/reportInputValidation.js";
import { getTimeZoneFromSwaStation } from "../util/timeZoneService";
import GenerateReportDownloadDialog from "./GenerateReportDownloadDialog.jsx";
import ConfirmationDialog from "./fundamentals/ConfirmationDialog.jsx";
import StarsDatePicker from "./fundamentals/StarsDatePicker.jsx";
import { CANCEL_WARNING_MESSAGE_ACTION_TYPE } from "../util/constants.js";

const useStyles = makeStyles((theme) => ({
  input: {
    width: "348px",
  },
  buttonContainer: {
    marginTop: "20px",
  },
  link: {
    display: "none",
  },
}));

const DailyTORReport = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [shiftDate, setShiftDate] = useState(null);
  const [shiftDateErrors, setShiftDateErrors] = useState("");
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(false);
  const [isGenerateConfirmDialogOpen, setIsGenerateConfirmDialogOpen] = useState(false);
  const [isGenerateDownloadDialogOpen, setIsGenerateDownloadDialogOpen] = useState(false);
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [isReportDownloadError, setIsReportDownloadError] = useState(false);
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const linkRef = useRef();

  useEffect(() => {
    if (shiftDate) {
      const shiftDateUpdated = shiftDate.setZone(getTimeZoneFromSwaStation(props.station), {
        keepLocalTime: true,
      });

      setShiftDate(shiftDateUpdated);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.station]);

  const resetState = () => {
    setIsReportDownloading(false);
    setIsReportDownloadError(false);
    setResponseMessage("");
    setDownloadedFile(null);
  };

  const isFormValidationSuccess = () => {
    const inputErrors = validateReportShiftDate(shiftDate);
    setShiftDateErrors(inputErrors);
    return inputErrors === "";
  };

  const buildGenerateReportPayload = () => {
    return {
      eid: userSession.eid,
      station: props.station,
      shiftDate: shiftDate.toMillis().toString(),
    };
  };

  const handleGenerateReport = () => {
    resetState();
    setIsReportDownloading(true);
    generateTORReport(buildGenerateReportPayload())
      .then((res) => res.json())
      .then(async (result) => {
        if (result.message) {
          setResponseMessage(result.message);
        } else {
          setDownloadedFile(result);
        }
        setIsReportDownloading(false);
      })
      .catch((err) => {
        setIsReportDownloadError(true);
        setIsReportDownloading(false);
      });
  };

  const handleCancelButtonClicked = () => {
    setIsCancelConfirmDialogOpen(true);
  };

  const handleCancelDialogNoButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
  };

  const handleCancelDialogYesButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
    props.onResetState();
  };

  const handleGenerateButtonClicked = () => {
    if (isFormValidationSuccess()) {
      setIsGenerateConfirmDialogOpen(true);
    }
  };

  const handleGenerateDialogNoButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
  };

  const handleGenerateDialogYesButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
    setIsGenerateDownloadDialogOpen(true);
    handleGenerateReport();
  };

  const handleDownloadDialogClose = () => {
    setIsGenerateDownloadDialogOpen(false);
  };

  const handleDownloadDialogDownloadButtonClicked = () => {
    const a = linkRef.current;
    a.download = downloadedFile.fileName;
    a.href = downloadedFile.downloadUrl;
    a.click();
    a.href = "";
    setIsGenerateDownloadDialogOpen(false);
    resetState();
    props.onResetState();
  };

  const handleShiftDateChange = (date) => {
    let inputErrors = validateReportShiftDate(date);
    if (inputErrors === "") {
      setShiftDate(date);
      setShiftDateErrors("");
    } else {
      setShiftDateErrors(inputErrors);
    }
  };

  return (
    <Grid container item data-testid="daily-tor-report">
      <Grid item xs={12}>
        <FormControl>
          <StarsDatePicker
            label="Shift Date"
            date={shiftDate}
            onChange={handleShiftDateChange}
            dateErrors={shiftDateErrors}
            minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.station))}
            className={classes.input}
          />
        </FormControl>
      </Grid>
      <Grid container item justifyContent="flex-end" spacing={2} xs={12} className={classes.buttonContainer}>
        <Grid item>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCancelButtonClicked}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={handleGenerateButtonClicked}
            size="large"
          >
            Generate
          </Button>
        </Grid>
      </Grid>
      {isCancelConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.SELECTIONS)}
          isDialogOpen={isCancelConfirmDialogOpen}
          handleNoButtonClicked={handleCancelDialogNoButtonClicked}
          handleYesButtonClicked={handleCancelDialogYesButtonClicked}
        />
      )}
      {isGenerateConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE}
          isDialogOpen={isGenerateConfirmDialogOpen}
          handleNoButtonClicked={handleGenerateDialogNoButtonClicked}
          handleYesButtonClicked={handleGenerateDialogYesButtonClicked}
        />
      )}
      {isGenerateDownloadDialogOpen && (
        <>
          <GenerateReportDownloadDialog
            isDownloading={isReportDownloading}
            isError={isReportDownloadError}
            isDialogOpen={isGenerateDownloadDialogOpen}
            onClose={handleDownloadDialogClose}
            onDownload={handleDownloadDialogDownloadButtonClicked}
            onRetry={handleGenerateReport}
            responseMessage={responseMessage}
          />
          <a className={classes.link} ref={linkRef} href="/">
            Download Report
          </a>
        </>
      )}
    </Grid>
  );
};

DailyTORReport.propTypes = {
  station: PropTypes.string.isRequired,
  onResetState: PropTypes.func.isRequired,
};

export default DailyTORReport;
